import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import en from './locales/en.json';
import {
  buildCrowdinTranslationFileUrl,
  FALLBACK_CROWDIN_CONTENT_PATHS,
  fetchCrowdinDistributionManifest,
  resolveCrowdinDistributionHash,
} from './crowdinDistribution';

let initPromise: Promise<void> | null = null;

/**
 * Web: bundled `en.json` only; Spanish and other locales load from Crowdin CDN
 * via i18next-http-backend. Non-browser (tests): bundled `en` only.
 */
export function initI18n(): Promise<void> {
  if (i18n.isInitialized) {
    return Promise.resolve();
  }
  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const isBrowser = typeof window !== 'undefined';

    if (!isBrowser) {
      await i18n.use(initReactI18next).init({
        lng: 'en',
        fallbackLng: 'en',
        supportedLngs: ['en'],
        resources: {
          en: { translation: en as Record<string, unknown> },
        },
        defaultNS: 'translation',
        ns: ['translation'],
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
        debug: false,
      });
      return;
    }

    const distributionHash = await resolveCrowdinDistributionHash();
    let pathByLanguage: Record<string, string> = { ...FALLBACK_CROWDIN_CONTENT_PATHS };
    let timestamp = 0;

    try {
      const manifest = await fetchCrowdinDistributionManifest(distributionHash);
      pathByLanguage = { ...FALLBACK_CROWDIN_CONTENT_PATHS, ...manifest.pathByLanguage };
      timestamp = manifest.timestamp;
    } catch (e) {
      console.warn('[i18n] Crowdin manifest fetch failed; using fallback content paths', e);
    }

    const supportedLngs = ['en', ...Object.keys(pathByLanguage)];

    /** `load: 'currentOnly'` keeps regional codes (e.g. `es-PA`) aligned with Crowdin manifest keys. */
    const loadPath = (languages: string | readonly string[]) => {
      const lng = Array.isArray(languages) ? languages[0] : languages;
      if (!lng || lng === 'en') {
        return '';
      }
      const contentPath = pathByLanguage[lng];
      if (!contentPath) {
        return '';
      }
      return buildCrowdinTranslationFileUrl(distributionHash, contentPath, timestamp);
    };

    await i18n
      .use(HttpBackend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        lng: 'en',
        fallbackLng: 'en',
        supportedLngs,
        // Regional Crowdin locales (e.g. es-PA) must keep their full code for `pathByLanguage` lookup.
        load: 'currentOnly',
        // If true, i18next normalizes regional codes for supportedLngs checks and may skip CDN fetch.
        nonExplicitSupportedLngs: false,
        partialBundledLanguages: true,
        resources: {
          en: {
            translation: en as Record<string, unknown>,
          },
        },
        defaultNS: 'translation',
        ns: ['translation'],
        backend: {
          loadPath,
          parse: (data: string) => JSON.parse(data) as Record<string, unknown>,
          crossDomain: true,
        },
        interpolation: { escapeValue: false },
        react: { useSuspense: false },
        debug: false,
      });
  })();

  return initPromise;
}

export default i18n;
